import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

export type DogSet = {
  petIds: string[];
  dogCount: number;
  puppyCount: number;
};

export type PetInfoCalloutResponse = {
  response: string;
  timestamp: number;
  dogSet: DogSet;
};

export type CalloutResponses = {
  [questionId: string]: PetInfoCalloutResponse[];
};

export function dogSetsMatch(a: DogSet, b: DogSet): boolean {
  return (
    JSON.stringify([...a.petIds].sort()) === JSON.stringify([...b.petIds].sort()) &&
    a.dogCount === b.dogCount &&
    a.puppyCount === b.puppyCount
  );
}

export interface StorageAdapter {
  getItem(): string | null | undefined;
  setItem(value: string): void;
  removeItem(): void;
}

/**
 * Creates storage functions for managing Pet Info Callout responses.
 * Stores ANY interaction with the callout: answer selections (yes/no/unsure) OR dismissal (X button)
 * Responses are tracked per dog set — the callout reappears if the user's dogs change
 */
export function createPetInfoCalloutStorage(adapter: StorageAdapter) {
  function getCalloutResponses(): CalloutResponses {
    try {
      const stored = adapter.getItem();
      if (!stored) return {};
      return JSON.parse(stored) as CalloutResponses;
    } catch (error) {
      captureError('Error reading pet info callout responses from storage');
      return {};
    }
  }

  function getCalloutResponse(questionId: string, dogSet: DogSet): PetInfoCalloutResponse | null {
    const responses = getCalloutResponses()[questionId];
    if (!responses?.length) return null;
    return responses.find((r) => dogSetsMatch(r.dogSet, dogSet)) || null;
  }

  function hasRespondedToCallout(questionId: string, dogSet: DogSet): boolean {
    return !!getCalloutResponse(questionId, dogSet);
  }

  function storeCalloutResponse(questionId: string, response: string, dogSet: DogSet): void {
    try {
      const allResponses = getCalloutResponses();
      const responsesForQuestion = allResponses[questionId] ?? [];

      // Deduplicate — skip if already responded for this dog set
      if (responsesForQuestion.some((r) => dogSetsMatch(r.dogSet, dogSet))) {
        return;
      }

      const newResponse: PetInfoCalloutResponse = {
        response,
        timestamp: Date.now(),
        dogSet: { ...dogSet, petIds: [...dogSet.petIds].sort() },
      };

      allResponses[questionId] = [...responsesForQuestion, newResponse];
      adapter.setItem(JSON.stringify(allResponses));
    } catch (error) {
      captureError('Error storing pet info callout response');
    }
  }

  // For debugging purposes
  function clearCalloutResponse(questionId: string): void {
    try {
      const responses = getCalloutResponses();
      delete responses[questionId];
      adapter.setItem(JSON.stringify(responses));
    } catch (error) {
      captureError('Error clearing pet info callout response');
    }
  }

  // For debugging purposes
  function clearAllCalloutResponses(): void {
    try {
      adapter.removeItem();
    } catch (error) {
      captureError('Error clearing all pet info callout responses');
    }
  }

  return {
    getCalloutResponse,
    hasRespondedToCallout,
    storeCalloutResponse,
    clearCalloutResponse,
    clearAllCalloutResponses,
  };
}
